<template>
  <div v-if="status === USER_STATUS.PENDING">
    <GuButton
      :key="status + 'PendingApprove'"
      aria-label="Approve Company Member"
      class="me-2"
      :disabled="actionInProgress"
      icon="bx-check"
      outline
      size="sm"
      variant="success"
      @click="triggerAction('approve')"
    >
      Approve
    </GuButton>

    <GuButton
      :key="status + 'PendingReject'"
      aria-label="Reject Company Member"
      :disabled="actionInProgress"
      icon="bx-block"
      outline
      size="sm"
      variant="danger"
      @click="triggerAction('reject')"
    >
      Reject
    </GuButton>
  </div>

  <div v-else :key="status">
    <GuButton
      v-if="status === USER_STATUS.APPROVED"
      aria-label="Disable Company Member"
      :disabled="actionInProgress"
      icon="bx-x"
      outline
      size="sm"
      variant="danger"
      @click="triggerAction('disable')"
    >
      Disable
    </GuButton>

    <GuButton
      v-else-if="status === USER_STATUS.DISABLED"
      aria-label="Enable Company Member"
      :disabled="actionInProgress"
      icon="bx-check"
      outline
      size="sm"
      variant="success"
      @click="triggerAction('enable')"
    >
      Enable
    </GuButton>

    <GuButton
      v-if="type && status !== USER_STATUS.DISABLED"
      aria-label="Toggle Company Member Role"
      class="ms-2"
      :disabled="actionInProgress"
      icon="bx-group"
      outline
      size="sm"
      @click="triggerAction('toggleRole')"
    >
      {{ toggleRoleLabel }}
    </GuButton>

    <GuDialog v-if="type && status !== USER_STATUS.DISABLED" v-model:open="isDeleteConfirmationDialogOpen">
      <GuDialogTrigger as-child>
        <GuButton
          aria-label="Remove User from Company"
          class="ms-2"
          icon="bx-trash"
          size="sm"
          variant="danger"
          outline
        >
          Remove
        </GuButton>
      </GuDialogTrigger>

      <GuDialogContent>
        <template #title>
          <h5 class="modal-title fs-18">Remove User from Company</h5>
        </template>

        <p>This action will remove the user <strong>{{ params.data.name }}</strong> from this company.</p>
        <p>They will no longer be able to access any company projects.</p>
        <p>Are you sure you want to continue?</p>

        <template #footer>
          <GuDialogClose as-child>
            <GuButton
              variant="secondary"
              :disabled="actionInProgress"
            >
              Cancel
            </GuButton>
          </GuDialogClose>

          <GuButton
            :loading="actionInProgress"
            @click="triggerAction('reject')"
          >
            Confirm
          </GuButton>
        </template>
      </GuDialogContent>
    </GuDialog>
  </div>
</template>

<script setup>
import { computed, ref, toRefs } from 'vue'

import { GuButton } from '@shared/gu-components/button'
import { GuDialog, GuDialogClose, GuDialogContent, GuDialogTrigger } from '@shared/gu-components/dialog'
import { useToast } from '@shared/gu-components/toast'

import { approveCompanyMember, deleteCompanyMember, disableCompanyMember, toggleCompanyMemberRole } from '@companies/api/companies.api'

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
})
const { params } = toRefs(props)

const { toast } = useToast()

const USER_STATUS = {
  APPROVED: 'APPROVED',
  DISABLED: 'DISABLED',
  PENDING: 'PENDING',
}

const isDeleteConfirmationDialogOpen = ref(false)

const status = ref(params.value.value)
const type = ref(params.value.data.type)
const user = ref(params.value.data.name)
const actionInProgress = ref(false)
const companyName = ref(props.params.companyName || '')

const toggleRoleLabel = computed(() => type.value === 'admin' ? 'To Member' : 'To Admin')

const { mutateAsync: approveCompanyMemberMutation } = approveCompanyMember(companyName.value, user.value)
const { mutateAsync: deleteCompanyMemberMutation } = deleteCompanyMember(companyName.value, user.value)
const { mutateAsync: disableCompanyMemberMutation } = disableCompanyMember(companyName.value, user.value)
const { mutateAsync: toggleCompanyMemberRoleMutation } = toggleCompanyMemberRole(companyName.value, user.value)

async function triggerAction (action) {
  actionInProgress.value = true

  try {
    const targetUser = user.value

    if (!targetUser) console.warn('Username not present in grid')
    if (!companyName.value) console.warn('Company(name) not present in grid')

    const actions = {
      approve: (company, user) => approveCompanyMemberMutation(company, user),
      disable: (company, user) => disableCompanyMemberMutation(company, user),
      enable: (company, user) => approveCompanyMemberMutation(company, user),
      reject: (company, user) => deleteCompanyMemberMutation(company, user),
      toggleRole: (company, user) => toggleCompanyMemberRoleMutation(company, user),
    }

    const response = await actions[action](companyName, targetUser)

    const rowNode = params.value.node

    for (const [key, value] of Object.entries(response)) {
      rowNode.setDataValue(key, value)
      if (key === 'type') type.value = value
    }
  } catch (ex) {
    console.error(ex)

    toast({
      variant: 'danger',
      description: ex.details || ex.message,
    })
  } finally {
    actionInProgress.value = false
  }
}
</script>

<style>
.actions-container > *:not(:last-child) {
  margin-right: .25rem;
}
</style>
